import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Popup from "reactjs-popup";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { closePopup, fetchPopupData, setPopupPagination } from "./ActionCreator";
import './Popup.scss';
import DataGuardCountryMap from './DataGuardCountryMap';
import { TABLE_COLS } from '../Utility/DetailsPopupConstant';
import { isNull } from "lodash";
import { COUNTRY, getHierarcyDataKey, getTotalHierarcyKey, LOCATION, DATASOURCE } from './ActionTypes';
import { SplitLayoutTemplate, TableTopChartTemplate, UserLocationHeader } from './PopupTemplates';
import { getPopupConfig, POPUP_TEMPLATES } from './PopupConfigurations';
import { DataGuardAlertStatusSnackBar } from '../DataGuardHelperComponents';

const DetailsPopup = () => {
  const dispatch = useDispatch();
  const popupOpen = useSelector((state) => state.DataguardReducer.popupOpen);
  let popupData = useSelector((state) => state.DataguardReducer.popupData);
  const popupLoader = useSelector((state) => state.DataguardReducer.popupLoader);
  const popupDetails = useSelector((state) => state.DataguardReducer.popupDetails);
  const isIncognito = useSelector((state) => state.DataguardReducer.incognito);
  const pagination = useSelector((state) => state.DataguardReducer.popupPagination);
  useEffect(() => {
    if (popupOpen && popupDetails) {
      dispatch(fetchPopupData());
    }
  }, [popupOpen, popupDetails, pagination, dispatch]);
  const rows = useMemo(() => {
    if (!isNull(popupData) && popupData.content) {
      return popupData.content.map((row, index) => ({ ...row, id: index }));
    }
    return [];
  }, [popupData]);
  const popupType = popupDetails?.[getHierarcyDataKey(COUNTRY)];
  let category = null;
  if (popupDetails) {
    category = getTotalHierarcyKey(popupDetails.hierarchy);
  }
  // Get isChildLevel flag from popupDetails to conditionally hide datasource column
  const hideDataSource = popupDetails?.isChildLevel || false;
  const cols = useMemo(() => {
    return TABLE_COLS(isIncognito, category, rows, hideDataSource);
  }, [isIncognito, category, rows, hideDataSource]);
  const renderSideCharts = () => {
    if (isNull(popupData) || isNull(popupData.content) || popupData.content.length === 0) {
      return <div className="no-data-message">No Data Available</div>;
    }
    const selectedPopupMapData = {
      citiesCircles: popupData.content
        .filter(item => {
          const lat = item.latitude;
          const lng = item.longitude;
          return lat && lng && 
                 lat !== "Not Found" && lat !== "Unknown" &&
                 lng !== "Not Found" && lng !== "Unknown" &&
                 lat !== null && lng !== null &&
                 lat !== "" && lng !== "";
        })
        .map(item => ({
          name: item.city || 'Unknown',
          users: item.users || 5,
          lat: item.latitude,
          lng: item.longitude,
        }))
    };
    return <div className="sys-guard-location-side-chart">
      <DataGuardCountryMap mapData={selectedPopupMapData} />
    </div>;
  }

  const renderTopChart = () => {
    // Placeholder for top chart implementation
    return <div className="chart-placeholder">Chart Coming Soon</div>;
  }

  const showLoader = () => {
    return <div className="global-loader-holder">
      <img src={Page_Loader} alt="loading" className="sysGuardPopUpLoader" />
    </div>;
  }

  const renderContent = (data) => {
    if (!data || !popupDetails) return null;
    
    const config = getPopupConfig(category);
    const handlePaginationChange = (newPagination) => {
      dispatch(setPopupPagination(newPagination));
    };

    const commonProps = {
      pagination,
      onPaginationChange: handlePaginationChange
    };

    const tableConfig = {
      rows,
      columns: cols,
      totalElements: data?.totalElements || 0
    };

    const header = (
      <UserLocationHeader
        popupData={popupData}
        location={popupType}
        isIncognito={isIncognito}
        title={config.title}
        subHeading={config.subHeading}
        showCountryFlag={config.showCountryFlag}
      />
    );

    // Render based on template type
    switch (config.template) {
      case POPUP_TEMPLATES.SPLIT_LAYOUT:
        return (
          <SplitLayoutTemplate
            header={header}
            tableConfig={tableConfig}
            sideChart={renderSideCharts()}
            {...commonProps}
          />
        );
      
      case POPUP_TEMPLATES.TABLE_TOP_CHART:
        return (
          <TableTopChartTemplate
            header={header}
            tableConfig={tableConfig}
            topChart={renderTopChart()}
            configuration={config}
            {...commonProps}
          />
        );
      
      default:
        return <div>No content available</div>;
    }
  };

  const onClose = () => {
    dispatch(closePopup());
  };
  return (
    <div className={popupOpen ? "sys-guard-backdrop" : ""}>
      <Popup
        className='dataguard-popup'
        open={popupOpen}
        onClose={onClose}
        modal
        closeOnDocumentClick={false}
        contentStyle={{ width: 'auto', minHeight: '500px', minWidth: '500px', maxWidth: '95vw', maxHeight: '90vh' }}
      >
        {popupLoader && showLoader()}
        {!popupLoader && !popupData && <div className="no-data-message">No Data Available</div>}
        <div className={`dataguard-popup-container sysguard-popup-container ${category}`}>
          { (!popupLoader && !popupData) ? <div className="no-data-message mrg-auto">No Data Available</div> : renderContent(popupData)}
          <button className="sys-guard-popup-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
      </Popup>
      <DataGuardAlertStatusSnackBar />
    </div>
  );
};

export default DetailsPopup;